import { computed, reactive, ref } from 'vue';

import analytics from '@/Analytics';
import initialState from '@/InitialState';
import { getGuestOrderToken, setGuestOrderToken } from '@/Components/Orders/OrderUtility';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';
import { useUserContextStore } from '@/Stores/Web/userContextStore';

// A guest leaves an email before paying - the token it buys them is what turns the fields read only.
export function useGuestCheckout() {
    const projectContextStore = useProjectContextStore();
    const userContextStore = useUserContextStore();

    const model = reactive({
        email: null,
        confirmationEmail: null,
        createAccount: false,
    });

    const orderToken = ref(getGuestOrderToken());

    const isUnauthenticated = computed(() => !userContextStore.userContext?.isAuthenticated);
    const areFieldsVisible = computed(() => isUnauthenticated.value
        && initialState.userAdditionalInfo.guestOrderToken === null);
    const areFieldsReadOnly = computed(() => !!orderToken.value);
    const needsOrderToken = computed(() => areFieldsVisible.value && !orderToken.value);
    const userEmail = computed(() => model.email || userContextStore.userContext?.user?.email);

    function validationRules() {
        return {
            email: () => areFieldsVisible.value ? { required: true, email: true } : {},
            confirmationEmail: () => areFieldsVisible.value
                ? { required: true, matchEmail: [model.email] }
                : {},
        };
    }

    function registerOrderToken(token) {
        orderToken.value = token;
        setGuestOrderToken(token);
    }

    function trackEmailCompleted() {
        analytics.track('email_input_completed', {
            'section': 'checkout-payment-form',
            'account_creation_opt_in': model.createAccount,
            'is_user_authenticated': false,
        }, projectContextStore.projectContext);
    }

    // Reactive, so that consumers reading it through one name do not have to unwrap the refs.
    return reactive({
        model,
        isUnauthenticated,
        areFieldsVisible,
        areFieldsReadOnly,
        needsOrderToken,
        userEmail,
        validationRules,
        registerOrderToken,
        trackEmailCompleted,
    });
}
